import { supabase } from '../lib/supabase';

export interface PotentialDuplicate {
  id: string;
  students: Student[];
  matchType: 'email_exact' | 'name_fuzzy' | 'phone_match';
  confidence: number;
  reason: string;
}

export interface Student {
  id: number;
  student_id: number | null;
  first_name: string;
  family_name: string;
  combined_name: string;
  email: string;
  phone_number: string | null;
  created_at: string;
  updated_at: string;
  semester_id: number | null;
  semester_year: string | null;
  address: string | null;
  home_country: string | null;
  major: string | null;
  program_level: string | null;
  marital_status: string | null;
  gender: string | null;
  special_interests: string | null;
  has_dietary_restrictions: boolean | null;
  dietary_restrictions_details: string | null;
  willing_to_share: boolean | null;
  vaccination_status: string | null;
  time_submitted: string | null;
}

export interface MergeRequest {
  keepStudentId: number;
  mergeStudentIds: number[];
  mergedData: Partial<Student>;
}

export class DuplicateDetectionService {
  
  // Main function to find all potential duplicates
  static async findPotentialDuplicates(): Promise<PotentialDuplicate[]> {
    try {
      const { data: students, error } = await supabase
        .from('students')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!students) return [];

      const duplicateGroups: PotentialDuplicate[] = [];
      const processedIds = new Set<number>();

      // Find exact email matches
      const emailMatches = this.findEmailDuplicates(students);
      emailMatches.forEach(group => {
        duplicateGroups.push(group);
        group.students.forEach(s => processedIds.add(s.id));
      });

      // Find fuzzy name matches (excluding already processed students)
      const remainingStudents = students.filter(s => !processedIds.has(s.id));
      const nameMatches = this.findNameDuplicates(remainingStudents);
      nameMatches.forEach(group => {
        duplicateGroups.push(group);
        group.students.forEach(s => processedIds.add(s.id));
      });

      // Find phone number matches (excluding already processed students)
      const finalRemainingStudents = students.filter(s => !processedIds.has(s.id));
      const phoneMatches = this.findPhoneDuplicates(finalRemainingStudents);
      phoneMatches.forEach(group => {
        duplicateGroups.push(group);
      });

      return duplicateGroups.sort((a, b) => b.confidence - a.confidence);
    } catch (error) {
      console.error('Error finding potential duplicates:', error);
      throw error;
    }
  }

  // Find students with exact email matches
  private static findEmailDuplicates(students: Student[]): PotentialDuplicate[] {
    const emailGroups = new Map<string, Student[]>();
    
    students.forEach(student => {
      const email = student.email.toLowerCase().trim();
      if (!emailGroups.has(email)) {
        emailGroups.set(email, []);
      }
      emailGroups.get(email)!.push(student);
    });

    const duplicates: PotentialDuplicate[] = [];
    emailGroups.forEach((studentsGroup, email) => {
      if (studentsGroup.length > 1) {
        duplicates.push({
          id: `email_${email}`,
          students: studentsGroup,
          matchType: 'email_exact',
          confidence: 0.95,
          reason: `Multiple students with same email: ${email}`
        });
      }
    });

    return duplicates;
  }

  // Find students with similar names using fuzzy matching
  private static findNameDuplicates(students: Student[]): PotentialDuplicate[] {
    const duplicates: PotentialDuplicate[] = [];
    const processedIds = new Set<number>();

    for (let i = 0; i < students.length; i++) {
      if (processedIds.has(students[i].id)) continue;

      const matches: Student[] = [students[i]];
      
      for (let j = i + 1; j < students.length; j++) {
        if (processedIds.has(students[j].id)) continue;

        const similarity = this.calculateNameSimilarity(students[i], students[j]);
        if (similarity > 0.8) {
          matches.push(students[j]);
        }
      }

      if (matches.length > 1) {
        matches.forEach(s => processedIds.add(s.id));
        
        const avgSimilarity = matches.length > 2 ? 0.85 : this.calculateNameSimilarity(matches[0], matches[1]);
        duplicates.push({
          id: `name_${matches[0].id}_${Date.now()}`,
          students: matches,
          matchType: 'name_fuzzy',
          confidence: Math.min(avgSimilarity, 0.9),
          reason: `Similar names: ${matches.map(s => s.combined_name).join(', ')}`
        });
      }
    }

    return duplicates;
  }

  // Find students with matching phone numbers
  private static findPhoneDuplicates(students: Student[]): PotentialDuplicate[] {
    const phoneGroups = new Map<string, Student[]>();
    
    students.forEach(student => {
      if (student.phone_number) {
        const normalizedPhone = this.normalizePhoneNumber(student.phone_number);
        if (normalizedPhone) {
          if (!phoneGroups.has(normalizedPhone)) {
            phoneGroups.set(normalizedPhone, []);
          }
          phoneGroups.get(normalizedPhone)!.push(student);
        }
      }
    });

    const duplicates: PotentialDuplicate[] = [];
    phoneGroups.forEach((studentsGroup, phone) => {
      if (studentsGroup.length > 1) {
        duplicates.push({
          id: `phone_${phone}`,
          students: studentsGroup,
          matchType: 'phone_match',
          confidence: 0.8,
          reason: `Multiple students with same phone: ${phone}`
        });
      }
    });

    return duplicates;
  }

  // Calculate similarity between two student names
  private static calculateNameSimilarity(student1: Student, student2: Student): number {
    const name1Parts = this.normalizeNameParts(student1);
    const name2Parts = this.normalizeNameParts(student2);

    // Check for exact matches in different orders
    if (this.areNamePartsEqual(name1Parts, name2Parts)) {
      return 1.0;
    }

    // Calculate Levenshtein distance for combined names
    const combinedName1 = `${name1Parts.first} ${name1Parts.family}`.toLowerCase();
    const combinedName2 = `${name2Parts.first} ${name2Parts.family}`.toLowerCase();
    
    const distance = this.levenshteinDistance(combinedName1, combinedName2);
    const maxLength = Math.max(combinedName1.length, combinedName2.length);
    const similarity = 1 - (distance / maxLength);

    return similarity;
  }

  // Normalize name parts for comparison
  private static normalizeNameParts(student: Student): { first: string, family: string } {
    return {
      first: student.first_name.toLowerCase().trim(),
      family: student.family_name.toLowerCase().trim()
    };
  }

  // Check if name parts are equal (accounting for potential swapped order)
  private static areNamePartsEqual(parts1: { first: string, family: string }, parts2: { first: string, family: string }): boolean {
    return (parts1.first === parts2.first && parts1.family === parts2.family) ||
           (parts1.first === parts2.family && parts1.family === parts2.first);
  }

  // Calculate Levenshtein distance between two strings
  private static levenshteinDistance(str1: string, str2: string): number {
    const matrix: number[][] = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }

  // Normalize phone number for comparison
  private static normalizePhoneNumber(phone: string): string | null {
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Skip if too short or too long
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      return null;
    }

    // For US numbers, normalize to 10 digits (remove country code if present)
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return digitsOnly.substring(1);
    }

    return digitsOnly;
  }

  // Merge duplicate students
  static async mergeStudents(mergeRequest: MergeRequest): Promise<void> {
    const { keepStudentId, mergeStudentIds, mergedData } = mergeRequest;

    try {
      await supabase.rpc('merge_duplicate_students', {
        keep_student_id: keepStudentId,
        merge_student_ids: mergeStudentIds,
        merged_data: mergedData
      });
    } catch (error) {
      console.error('Error merging students:', error);
      throw error;
    }
  }

  // Get detailed student information for comparison
  static async getStudentDetails(studentIds: number[]): Promise<Student[]> {
    try {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .in('id', studentIds);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting student details:', error);
      throw error;
    }
  }

  // Mark students as not duplicates (to prevent future false positives)
  static async markAsNotDuplicate(studentIds: number[]): Promise<void> {
    // This would require a new table to track "confirmed not duplicates"
    // For now, we'll implement this as a simple log
    console.log('Marking as not duplicate:', studentIds);
    
    // TODO: Implement a proper "not_duplicate_pairs" table if needed
  }
}